import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

// ─────────────────────────────────────────
// useServiceTestimonials
//
// Loads the testimonials collection stored in platform_settings under
// the key `service_testimonials`. Each entry can optionally be scoped
// to a catalog slug, entries without a slug are shown on all landing
// pages, entries with a slug are shown only on that service's page.
//
// Schema (JSON value in platform_settings):
//   [
//     { name, city, text, slug? }
//   ]
// ─────────────────────────────────────────

export interface Testimonial {
  name: string;
  city: string;
  text: string;
  slug?: string; // optional, if set, only shown on /servicos/{slug}
}

// Shared in-memory cache (5 min) across all landing pages.
let cache: { list: Testimonial[]; at: number } | null = null;
const CACHE_TTL_MS = 5 * 60 * 1000;

async function loadAll(): Promise<Testimonial[]> {
  if (cache && Date.now() - cache.at < CACHE_TTL_MS) return cache.list;

  const { data } = await supabase
    .from('platform_settings')
    .select('value')
    .eq('key', 'service_testimonials')
    .maybeSingle();

  if (!data?.value) {
    cache = { list: [], at: Date.now() };
    return [];
  }

  try {
    const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
    const list = Array.isArray(parsed) ? parsed as Testimonial[] : [];
    cache = { list, at: Date.now() };
    return list;
  } catch {
    return [];
  }
}

export function clearTestimonialsCache() {
  cache = null;
}

export function useServiceTestimonials(slug: string | undefined): Testimonial[] {
  const [items, setItems] = useState<Testimonial[]>([]);

  useEffect(() => {
    let active = true;
    loadAll().then(all => {
      if (!active) return;
      // Entries scoped to this slug take priority; generic entries
      // (no slug) fill in if there are fewer than 3 specific ones.
      const specific = slug ? all.filter(t => t.slug === slug) : [];
      const generic  = all.filter(t => !t.slug);
      const merged = [...specific, ...generic].slice(0, 3);
      setItems(merged);
    });
    return () => { active = false; };
  }, [slug]);

  return items;
}
